import React, { FC, useContext } from 'react';
import { useMutation } from '@apollo/client';
import { Button, Tooltip } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { mintApiDocsAutoLoginTokenTemplate } from 'apollo/templates/api-client';
import { CONSTANTS } from 'config/constants';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { OrganizationRef } from './types';

type OpenApiDocsButtonProps = {
    organization: OrganizationRef | null;
};

/**
 * Opens the Public Supplier API documentation in a new tab. Calls the
 * `mintApiDocsAutoLoginToken` mutation to get a short-lived JWT, then
 * navigates to `<PUBLIC_API_URL>/docs/login?token=...` which sets a
 * 4-day session cookie and redirects to the docs.
 *
 * The page itself is also reachable directly with HTTP basic auth using
 * the API_USERNAME / API_PASSWORD configured in customerSettings; this
 * button just removes the need to type them.
 */
const OpenApiDocsButton: FC<OpenApiDocsButtonProps> = ({ organization }: OpenApiDocsButtonProps) => {
    const { t: translate } = useTranslation(['app-settings', 'common']);
    const { setAppToasterContent } = useContext(AppToasterContext);

    const [mintToken, { loading }] = useMutation<{ mintApiDocsAutoLoginToken: string }>(
        mintApiDocsAutoLoginTokenTemplate,
    );

    const publicApiUrl = CONSTANTS.PUBLIC_API_URL;
    const disabled = !organization || !publicApiUrl || loading;

    const handleClick = async () => {
        if (!organization || !publicApiUrl) return;
        try {
            const result = await mintToken({ variables: { organizationId: organization._id } });
            const token = result.data?.mintApiDocsAutoLoginToken;
            if (!token) {
                throw new Error('No token returned from mintApiDocsAutoLoginToken');
            }
            const url = `${publicApiUrl.replace(/\/$/, '')}/docs/login?token=${encodeURIComponent(token)}`;
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch {
            setAppToasterContent({
                isOpen: true,
                message: translate('app-settings:OPEN_API_DOCS_FAILED'),
                severity: 'error',
            });
        }
    };

    const tooltip = !organization
        ? translate('app-settings:OPEN_API_DOCS_NO_ORG')
        : translate('app-settings:OPEN_API_DOCS_HELP');

    return (
        <Tooltip title={tooltip}>
            <span>
                <Button startIcon={<OpenInNewIcon />} onClick={handleClick} disabled={disabled}>
                    {translate('app-settings:OPEN_API_DOCS')}
                </Button>
            </span>
        </Tooltip>
    );
};

export default OpenApiDocsButton;
